import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Fingerprint, X } from "lucide-react";
import { useAuth } from "../lib/AuthContext";
import { enrollPasskey, listPasskeys, passkeySupported, platformPasskeyAvailable } from "../lib/passkey";
import { formatApiError } from "../lib/api";
import { toast } from "sonner";

const DISMISS_KEY = "rwo_passkey_prompted";

// One-time, non-intrusive nudge shown after login on devices that support
// platform passkeys, when the user hasn't enrolled one yet. Persisted per
// device so we never nag repeatedly.
export default function PasskeyEnrollPrompt() {
    const { user } = useAuth();
    const [show, setShow] = useState(false);
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            if (!user) return;
            try {
                if (window.localStorage.getItem(DISMISS_KEY)) return;
            } catch {
                return;
            }
            if (!passkeySupported()) return;
            const platform = await platformPasskeyAvailable();
            if (!platform) return;
            try {
                const existing = await listPasskeys();
                if (!cancelled && existing.length === 0) setShow(true);
            } catch {
                // ignore
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [user]);

    const dismiss = () => {
        try {
            window.localStorage.setItem(DISMISS_KEY, "1");
        } catch {
            // ignore
        }
        setShow(false);
    };

    const handleEnroll = async () => {
        setBusy(true);
        try {
            await enrollPasskey("This device");
            toast.success("Passkey added — next time just use biometrics.");
            dismiss();
        } catch (err) {
            if (err?.name === "NotAllowedError" || err?.name === "AbortError") {
                // user cancelled the native sheet — keep prompt, let them retry
            } else {
                toast.error(formatApiError(err) || "Couldn't set up a passkey right now.");
            }
        } finally {
            setBusy(false);
        }
    };

    if (!show) return null;

    return (
        <div
            data-testid="passkey-enroll-prompt"
            className="fixed z-[60] left-1/2 -translate-x-1/2 bottom-[calc(5rem+env(safe-area-inset-bottom))] sm:bottom-6 w-[calc(100%-2rem)] max-w-sm rounded-xl bg-midnight-pine text-white shadow-2xl border border-white/10 p-4"
        >
            <button
                data-testid="passkey-prompt-dismiss"
                onClick={dismiss}
                className="absolute top-2.5 right-2.5 p-1 rounded-md text-white/60 hover:text-white hover:bg-white/10 transition-all"
                aria-label="Dismiss"
            >
                <X className="w-4 h-4" />
            </button>
            <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-md bg-ember-orange/20 flex items-center justify-center shrink-0">
                    <Fingerprint className="w-5 h-5 text-ember-orange" />
                </div>
                <div className="min-w-0 pr-4">
                    <div className="font-display font-bold text-base leading-tight">Faster sign-ins</div>
                    <p className="text-xs text-white/70 mt-1">
                        Set up a passkey to log in with Face ID, Touch ID, or your fingerprint — no password needed.
                    </p>
                </div>
            </div>
            <div className="flex items-center gap-2 mt-3">
                <button
                    data-testid="passkey-prompt-enroll"
                    onClick={handleEnroll}
                    disabled={busy}
                    className="flex-1 py-2 rounded-md bg-ember-orange text-white text-sm font-bold hover:bg-ember-dark active:scale-95 disabled:opacity-60 transition-all"
                >
                    {busy ? "Follow the prompt…" : "Set up passkey"}
                </button>
                <button
                    onClick={dismiss}
                    className="px-3 py-2 rounded-md text-white/70 text-sm font-semibold hover:text-white transition-all"
                >
                    Not now
                </button>
            </div>
        </div>
    );
}
